'use client';

import { createClient } from '@/lib/supabase/client';

export interface Service {
  id: string;
  title: string;
  description: string;
  price: number;
  duration: string;
  icon: string;
  status: string;
  loyaltyPointsEarned: number;
}

export interface TimeSlot {
  id: string;
  time: string;
  period: string;
  isAvailable: boolean;
  maxBookings: number;
  currentBookings: number;
}

export interface Order {
  id: string;
  orderNumber: string;
  customerId: string;
  serviceId: string;
  pickupDate: string;
  pickupTime: string;
  deliveryAddress: string;
  landmark?: string;
  specialInstructions?: string;
  status: string;
  paymentMethod: string;
  paymentStatus: string;
  totalAmount: number;
  loyaltyPointsEarned: number;
  loyaltyPointsUsed: number;
  assignedStaffId?: string;
  estimatedCompletion?: string;
  actualCompletion?: string;
  createdAt: string;
}

export interface OrderStatusHistory {
  id: string;
  orderId: string;
  status: string;
  notes?: string;
  updatedBy?: string;
  createdAt: string;
}

export interface LoyaltyTransaction {
  id: string;
  customerId: string;
  orderId?: string;
  pointsChange: number;
  transactionType: string;
  description?: string;
  balanceAfter: number;
  createdAt: string;
}

export interface Equipment {
  id: string;
  name: string;
  equipmentType: string;
  status: string;
  capacity?: string;
  lastMaintenanceDate?: string;
  nextMaintenanceDate?: string;
  notes?: string;
}

export interface Inventory {
  id: string;
  itemName: string;
  category: string;
  quantity: number;
  unit: string;
  reorderLevel: number;
  costPerUnit?: number;
  supplier?: string;
  lastRestockedDate?: string;
  notes?: string;
}

export const orderService = {
  async getAll(userId: string) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('orders')
      .select(`
        *,
        service:services(*),
        customer:user_profiles!orders_customer_id_fkey(*)
      `)
      .eq('customer_id', userId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map((row: any) => ({
      id: row.id,
      orderNumber: row.order_number,
      customerId: row.customer_id,
      serviceId: row.service_id,
      pickupDate: row.pickup_date,
      pickupTime: row.pickup_time,
      deliveryAddress: row.delivery_address,
      landmark: row.landmark,
      specialInstructions: row.special_instructions,
      status: row.status,
      paymentMethod: row.payment_method,
      paymentStatus: row.payment_status,
      totalAmount: row.total_amount,
      loyaltyPointsEarned: row.loyalty_points_earned,
      loyaltyPointsUsed: row.loyalty_points_used,
      assignedStaffId: row.assigned_staff_id,
      estimatedCompletion: row.estimated_completion,
      actualCompletion: row.actual_completion,
      createdAt: row.created_at,
      service: row.service,
      customer: row.customer,
    })) || [];
  },

  async create(order: {
    customerId: string;
    serviceId: string;
    pickupDate: string;
    pickupTime: string;
    deliveryAddress: string;
    landmark?: string;
    specialInstructions?: string;
    paymentMethod: string;
    totalAmount: number;
    loyaltyPointsEarned: number;
  }) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Generate order number
    const { data: orderNumberData, error: orderNumberError } = await supabase
      .rpc('generate_order_number');

    if (orderNumberError) throw orderNumberError;

    const { data, error } = await supabase
      .from('orders')
      .insert({
        order_number: orderNumberData,
        customer_id: order.customerId,
        service_id: order.serviceId,
        pickup_date: order.pickupDate,
        pickup_time: order.pickupTime,
        delivery_address: order.deliveryAddress,
        landmark: order.landmark,
        special_instructions: order.specialInstructions,
        payment_method: order.paymentMethod,
        total_amount: order.totalAmount,
        loyalty_points_earned: order.loyaltyPointsEarned,
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      orderNumber: data.order_number,
      customerId: data.customer_id,
      serviceId: data.service_id,
      pickupDate: data.pickup_date,
      pickupTime: data.pickup_time,
      deliveryAddress: data.delivery_address,
      landmark: data.landmark,
      specialInstructions: data.special_instructions,
      status: data.status,
      paymentMethod: data.payment_method,
      paymentStatus: data.payment_status,
      totalAmount: data.total_amount,
      loyaltyPointsEarned: data.loyalty_points_earned,
      createdAt: data.created_at,
    };
  },

  async getOrderHistory(orderId: string) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('order_status_history')
      .select('*')
      .eq('order_id', orderId)
      .order('created_at', { ascending: true });

    if (error) throw error;

    return data?.map((row: any) => ({
      id: row.id,
      orderId: row.order_id,
      status: row.status,
      notes: row.notes,
      updatedBy: row.updated_by,
      createdAt: row.created_at,
    })) || [];
  },
};

export const serviceService = {
  async getAll() {
    const supabase = createClient();

    const { data, error } = await supabase
      .from('services')
      .select('*')
      .eq('status', 'active')
      .order('created_at', { ascending: true });

    if (error) throw error;

    return data?.map((row: any) => ({
      id: row.id,
      title: row.title,
      description: row.description,
      price: row.price,
      duration: row.duration,
      icon: row.icon,
      status: row.status,
      loyaltyPointsEarned: row.loyalty_points_earned,
    })) || [];
  },
};

export const timeSlotService = {
  async getAll() {
    const supabase = createClient();

    const { data, error } = await supabase
      .from('time_slots')
      .select('*')
      .order('time', { ascending: true });

    if (error) throw error;

    return data?.map((row: any) => ({
      id: row.id,
      time: row.time,
      period: row.period,
      isAvailable: row.is_available,
      maxBookings: row.max_bookings,
      currentBookings: row.current_bookings,
    })) || [];
  },
};

export const loyaltyService = {
  async getTransactions(customerId: string) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('loyalty_transactions')
      .select('*')
      .eq('customer_id', customerId)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map((row: any) => ({
      id: row.id,
      customerId: row.customer_id,
      orderId: row.order_id,
      pointsChange: row.points_change,
      transactionType: row.transaction_type,
      description: row.description,
      balanceAfter: row.balance_after,
      createdAt: row.created_at,
    })) || [];
  },

  async getCampaigns() {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('loyalty_campaigns')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map((row: any) => ({
      id: row.id,
      name: row.name,
      description: row.description,
      campaignType: row.campaign_type,
      status: row.status,
      startDate: row.start_date,
      endDate: row.end_date,
      targetSegment: row.target_segment,
      eligibilityCriteria: row.eligibility_criteria,
      promotionConfig: row.promotion_config,
      budgetLimit: row.budget_limit,
      currentSpend: row.current_spend,
      createdBy: row.created_by,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
    })) || [];
  },

  async createCampaign(campaign: any) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('loyalty_campaigns')
      .insert({
        name: campaign.name,
        description: campaign.description,
        campaign_type: campaign.campaignType,
        status: campaign.status || 'draft',
        start_date: campaign.startDate,
        end_date: campaign.endDate,
        target_segment: campaign.targetSegment,
        eligibility_criteria: campaign.eligibilityCriteria,
        promotion_config: campaign.promotionConfig,
        budget_limit: campaign.budgetLimit,
        created_by: user.id,
      })
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      name: data.name,
      description: data.description,
      campaignType: data.campaign_type,
      status: data.status,
      startDate: data.start_date,
      endDate: data.end_date,
      targetSegment: data.target_segment,
      eligibilityCriteria: data.eligibility_criteria,
      promotionConfig: data.promotion_config,
      budgetLimit: data.budget_limit,
      currentSpend: data.current_spend,
      createdBy: data.created_by,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async updateCampaign(campaignId: string, updates: any) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const updateData: any = {};
    if (updates.name !== undefined) updateData.name = updates.name;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.status !== undefined) updateData.status = updates.status;
    if (updates.startDate !== undefined) updateData.start_date = updates.startDate;
    if (updates.endDate !== undefined) updateData.end_date = updates.endDate;
    if (updates.targetSegment !== undefined) updateData.target_segment = updates.targetSegment;
    if (updates.eligibilityCriteria !== undefined) updateData.eligibility_criteria = updates.eligibilityCriteria;
    if (updates.promotionConfig !== undefined) updateData.promotion_config = updates.promotionConfig;
    if (updates.budgetLimit !== undefined) updateData.budget_limit = updates.budgetLimit;

    const { data, error } = await supabase
      .from('loyalty_campaigns')
      .update(updateData)
      .eq('id', campaignId)
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      name: data.name,
      description: data.description,
      campaignType: data.campaign_type,
      status: data.status,
      startDate: data.start_date,
      endDate: data.end_date,
      targetSegment: data.target_segment,
      eligibilityCriteria: data.eligibility_criteria,
      promotionConfig: data.promotion_config,
      budgetLimit: data.budget_limit,
      currentSpend: data.current_spend,
      createdBy: data.created_by,
      createdAt: data.created_at,
      updatedAt: data.updated_at,
    };
  },

  async getCampaignMetrics(campaignId: string) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('campaign_metrics')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('date', { ascending: false });

    if (error) throw error;

    return data?.map((row: any) => ({
      id: row.id,
      campaignId: row.campaign_id,
      date: row.date,
      participantsCount: row.participants_count,
      pointsAwarded: row.points_awarded,
      pointsRedeemed: row.points_redeemed,
      revenueImpact: row.revenue_impact,
      engagementRate: row.engagement_rate,
      conversionRate: row.conversion_rate,
      createdAt: row.created_at,
    })) || [];
  },

  async getCampaignParticipants(campaignId: string) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('campaign_participants')
      .select(`
        *,
        customer:user_profiles(full_name, email, loyalty_points)
      `)
      .eq('campaign_id', campaignId)
      .order('participated_at', { ascending: false });

    if (error) throw error;

    return data?.map((row: any) => ({
      id: row.id,
      campaignId: row.campaign_id,
      customerId: row.customer_id,
      pointsEarned: row.points_earned,
      participatedAt: row.participated_at,
      customer: row.customer ? {
        fullName: row.customer.full_name,
        email: row.customer.email,
        loyaltyPoints: row.customer.loyalty_points,
      } : null,
    })) || [];
  },
};

export const equipmentService = {
  async getAll() {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('equipment')
      .select('*')
      .order('name', { ascending: true });

    if (error) throw error;

    return data?.map((row: any) => ({
      id: row.id,
      name: row.name,
      equipmentType: row.equipment_type,
      status: row.status,
      capacity: row.capacity,
      lastMaintenanceDate: row.last_maintenance_date,
      nextMaintenanceDate: row.next_maintenance_date,
      notes: row.notes,
    })) || [];
  },

  async updateStatus(equipmentId: string, status: string) {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('equipment')
      .update({ status })
      .eq('id', equipmentId)
      .select()
      .single();

    if (error) throw error;

    return {
      id: data.id,
      name: data.name,
      equipmentType: data.equipment_type,
      status: data.status,
      capacity: data.capacity,
    };
  },
};

export const inventoryService = {
  async getAll() {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('inventory')
      .select('*')
      .order('item_name', { ascending: true });

    if (error) throw error;

    return data?.map((row: any) => ({
      id: row.id,
      itemName: row.item_name,
      category: row.category,
      quantity: row.quantity,
      unit: row.unit,
      reorderLevel: row.reorder_level,
      costPerUnit: row.cost_per_unit,
      supplier: row.supplier,
      lastRestockedDate: row.last_restocked_date,
      notes: row.notes,
    })) || [];
  },

  async getLowStock() {
    const supabase = createClient();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('inventory')
      .select('*')
      .filter('quantity', 'lte', 'reorder_level')
      .order('quantity', { ascending: true });

    if (error) throw error;

    return data?.map((row: any) => ({
      id: row.id,
      itemName: row.item_name,
      category: row.category,
      quantity: row.quantity,
      unit: row.unit,
      reorderLevel: row.reorder_level,
    })) || [];
  },
};